/**
 * Money helpers. All amounts are integer minor units ("fils" for AED: 1 AED =
 * 100 fils) — never floats. Percentages are basis points (4000 = 40%).
 */

export const DEFAULT_CURRENCY = "AED" as const;
export const BASIS_POINTS_100_PERCENT = 10_000;

/** Convert a major-unit amount ("8250.5" / 8250.5) to minor units, rounded half-up. */
export function toMinor(major: number | string): number {
  const value =
    typeof major === "string" ? Number(major.replace(/,/g, "")) : major;
  if (!Number.isFinite(value)) throw new Error(`Invalid amount: ${major}`);
  return Math.round(value * 100 + (value >= 0 ? 1e-9 : -1e-9));
}

/** Minor units → major-unit number (for display maths only). */
export function toMajor(minor: number): number {
  return minor / 100;
}

/** Sum minor-unit amounts. */
export function sumMinor(values: readonly number[]): number {
  return values.reduce((acc, v) => acc + v, 0);
}

/** `percentBp` of `totalMinor`, rounded half-up to the nearest fils. */
export function percentOfMinor(totalMinor: number, percentBp: number): number {
  return Math.round((totalMinor * percentBp) / BASIS_POINTS_100_PERCENT);
}

/**
 * Split `totalMinor` proportionally to `weights` so the parts always add up to
 * the total exactly (largest-remainder method; ties go to earlier parts).
 */
export function splitByWeights(
  totalMinor: number,
  weights: readonly number[],
): number[] {
  const weightSum = sumMinor(weights);
  if (weights.length === 0 || weightSum <= 0) return weights.map(() => 0);
  const exact = weights.map((w) => (totalMinor * w) / weightSum);
  const floors = exact.map((x) => Math.floor(x));
  let remainder = totalMinor - sumMinor(floors);
  const order = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of order) {
    if (remainder <= 0) break;
    floors[i] += 1;
    remainder -= 1;
  }
  return floors;
}

/** "AED 8,250.00" — the studio's standard amount format. */
export function formatMinor(
  minor: number,
  currency: string = DEFAULT_CURRENCY,
): string {
  const sign = minor < 0 ? "-" : "";
  const abs = Math.abs(minor);
  const major = Math.floor(abs / 100).toLocaleString("en-US");
  const fils = String(abs % 100).padStart(2, "0");
  return `${sign}${currency} ${major}.${fils}`;
}

/** Plain number string for inputs: 825000 → "8250.00". */
export function minorToInputString(minor: number): string {
  return (minor / 100).toFixed(2);
}
